import React, {useState} from 'react';
import {List, useRedirect, useListContext, useTranslate, usePermissions} from 'react-admin';
import {Box, Typography} from '@mui/material';
import {AddRounded} from '@mui/icons-material';
import {t} from '../themes/statamicTokens';
import PageHeader from '../components/statamic/PageHeader';
import PrimaryButton from '../components/statamic/PrimaryButton';
import SavedViewsTabs from '../components/statamic/SavedViewsTabs';
import FilterBar, {FilterDropdown} from '../components/statamic/FilterBar';
import StatamicTable from '../components/statamic/StatamicTable';
import ScopeBanner from '../components/statamic/ScopeBanner';
import useScopedFilter from '../scope/useScopedFilter';
import StatusPill from '../components/statamic/StatusPill';
import hasPermission from '../auth/hasPermission';

const LANGUAGE_LABEL = {
    nl: 'Nederlands',
    en: 'English',
    de: 'Deutsch',
    ru: 'Русский',
    hy: 'Հայերեն',
};

const stripHtml = (html) => {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
};

const truncate = (str, n = 100) => {
    if (!str) return '';
    return str.length > n ? `${str.slice(0, n).trim()}…` : str;
};

const SurfaceCard = ({children}) => (
    <Box
        sx={{
            backgroundColor: t.color.surface,
            border: `1px solid ${t.color.border.default}`,
            borderRadius: `${t.radius.md}px`,
            mx: 3,
            mb: 3,
            overflow: 'hidden',
        }}
    >
        {children}
    </Box>
);

const MailSettingsListInner = () => {
    const redirect = useRedirect();
    const translate = useTranslate();
    const {permissions} = usePermissions();
    const {filterValues, setFilters, displayedFilters} = useListContext();
    const [activeViewId, setActiveViewId] = useState('all');
    useScopedFilter('umbrellaOnly');

    const canSeeCharity = hasPermission(permissions, ['show'], 'umbrella');

    const views = [
        {id: 'all', label: translate('All')},
        {id: 'nl',  label: 'NL'},
        {id: 'en',  label: 'EN'},
        {id: 'de',  label: 'DE'},
    ];

    const handleViewSelect = (id) => {
        setActiveViewId(id);
        if (id === 'all') setFilters({...filterValues, language_code: undefined}, displayedFilters);
        else setFilters({...filterValues, language_code: id}, displayedFilters);
    };

    const updateFilter = (src, v) => setFilters({...filterValues, [src]: v}, displayedFilters);
    const clearFilter = (src) => {
        const next = {...filterValues};
        delete next[src];
        setFilters(next, displayedFilters);
    };

    const columns = [
        ...(canSeeCharity ? [{
            key: 'charity', label: translate('Umbrella'), sortable: false,
            render: (r) => (
                <Typography sx={{fontSize: `${t.type.bodyStrong.size}px`, fontWeight: 500, color: t.color.text.primary}}>
                    {r.charity_name || `#${r.charity_id}`}
                </Typography>
            ),
        }] : []),
        {
            key: 'language', label: translate('Language'), width: 140,
            render: (r) => (
                <StatusPill variant="neutral">
                    {LANGUAGE_LABEL[r.language_code] || String(r.language_code || '').toUpperCase()}
                </StatusPill>
            ),
        },
        {
            key: 'welcome', label: translate('Welcome text'),
            render: (r) => (
                <Typography
                    sx={{
                        fontSize: `${t.type.small.size}px`,
                        color: t.color.text.secondary,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        lineHeight: 1.4,
                    }}
                >
                    {truncate(stripHtml(r.welcome_text), 140)}
                </Typography>
            ),
        },
    ];

    const rowActions = (record) => [
        {label: translate('View'),                onClick: () => redirect('show', 'mailSettings', record.id)},
        {label: translate('Edit Registration'),  onClick: () => redirect('edit', 'mailSettings', record.id)},
        {label: translate('Edit Reminder'),      onClick: () => { window.location.hash = `#/mailSettings/${record.id}/1`; }},
        {label: translate('Edit Daily new'),     onClick: () => { window.location.hash = `#/mailSettings/${record.id}/2`; }},
    ];

    return (
        <Box sx={{backgroundColor: t.color.page, minHeight: '100%'}}>
            <PageHeader
                title={translate('Mail settings')}
                subtitle={translate('Email templates per umbrella and language')}
                actions={
                    <PrimaryButton
                        variant="primary"
                        startIcon={<AddRounded sx={{fontSize: 18}}/>}
                        onClick={() => redirect('create', 'mailSettings')}
                    >
                        {translate('Create mail settings')}
                    </PrimaryButton>
                }
            />
            <SurfaceCard>
                <ScopeBanner/>
                <SavedViewsTabs
                    views={views}
                    activeId={activeViewId}
                    onSelect={handleViewSelect}
                    onAdd={() => {}}
                />
                <FilterBar
                    searchValue={filterValues?.q}
                    onSearchChange={(q) => setFilters({...filterValues, q}, displayedFilters)}
                    placeholder={translate('Search mail settings')}
                    filters={
                        <FilterDropdown
                            label={translate('Language')}
                            source="language_code"
                            value={filterValues?.language_code}
                            onChange={updateFilter}
                            onClear={clearFilter}
                            options={[
                                {id: 'nl', name: 'Nederlands'},
                                {id: 'en', name: 'English'},
                                {id: 'de', name: 'Deutsch'},
                                {id: 'ru', name: 'Русский'},
                                {id: 'hy', name: 'Հայերեն'},
                            ]}
                        />
                    }
                />
                <StatamicTable
                    columns={columns}
                    rowActions={rowActions}
                    onRowClick={(record) => redirect('show', 'mailSettings', record.id)}
                />
            </SurfaceCard>
        </Box>
    );
};

const MailSettingsListStatamic = (props) => (
    <List
        {...props}
        perPage={25}
        actions={false}
        pagination={false}
        component={Box}
        sx={{
            backgroundColor: t.color.page,
            '& .RaList-main': {backgroundColor: t.color.page},
        }}
    >
        <MailSettingsListInner/>
    </List>
);

export default MailSettingsListStatamic;
